import WhatsAppButton from "@/components/WhatsAppButton";
import { Helmet } from "react-helmet-async";
import { motion, useScroll, useTransform, useInView, useSpring, useMotionValue, animate } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Users, Baby, BookOpen, GraduationCap, BarChart3, Smartphone,
  Shield, Globe, Zap, ChevronLeft, Star, Check, ArrowLeft,
  ClipboardCheck, MessageSquare, Sparkles, Heart, Plus,
  Gamepad2, Video, FileText, Brain, QrCode, CalendarOff,
  Receipt, Headphones, Camera, PenTool, Bell, Lock, Download,
  Wallet, BookOpenCheck, CreditCard, Music, Puzzle, Eye, Activity, Palette
} from "lucide-react";
import { useRef, useEffect, useState } from "react";
import { plans as subscriptionPlans, addOns, ANNUAL_DISCOUNT, type PlanDef } from "@/components/subscription/plans-data";

const FAQ_ITEMS = [
  { q: "هل يوجد فترة تجربة مجانية؟", a: "نعم، نوفر فترة تجربة مجانية لمدة 15 يوماً على جميع الباقات بدون الحاجة لبطاقة ائتمان. يمكنك تجربة كل المميزات قبل اتخاذ قرار الاشتراك." },
  { q: "هل أقدر أبدأ بالنظام الإداري فقط وأضيف التعليمي لاحقاً؟", a: "بالتأكيد! نظامنا مصمم بشكل مرن. تقدر تبدأ بباقة Basic الإدارية (١٠٠٠ ج.م/شهرياً) وتضيف النظام التعليمي كإضافة منفصلة (١٥٠٠ ج.م) في أي وقت." },
  { q: "كيف يتم تسجيل حضور الأطفال؟", a: "يتم تسجيل الحضور عبر مسح كود QR مخصص لكل طفل أو موظف. يمكنك أيضاً التسجيل اليدوي. النظام يولد تقارير حضور تفصيلية يومية وأسبوعية وشهرية." },
  { q: "هل يمكن لأولياء الأمور متابعة أطفالهم؟", a: "نعم، نوفر بوابة متكاملة لأولياء الأمور تشمل: متابعة الدروس والواجبات، الاطلاع على الحضور والغياب، استلام الفواتير، التواصل المباشر مع المعلمات، وطلب الإجازات." },
  { q: "ما هي الألعاب التعليمية المتوفرة؟", a: "نوفر ألعاب تعليمية تفاعلية تشمل: الحروف العربية والإنجليزية، الأرقام والحساب، الأشكال والألوان، العلوم والطبيعة، التربية الإسلامية، والمنطق. كل لعبة لها مستويات صعوبة مع تتبع أداء الطفل." },
  { q: "هل يدعم النظام توليد الامتحانات تلقائياً؟", a: "نعم! يمكنك رفع نص الدرس وسيقوم الذكاء الاصطناعي بتوليد أسئلة متنوعة (اختيار من متعدد، صح وخطأ، مقالي) مع بنك أسئلة متكامل يمكنك التعديل عليه." },
  { q: "كيف يعمل نظام الفواتير والمالية؟", a: "يمكنك إنشاء قوالب رسوم مخصصة (أقساط شهرية، رسوم نشاطات، باصات) وتوليد فواتير تلقائية لكل طفل. يتتبع النظام المدفوعات والمتأخرات مع تقارير مالية شاملة." },
  { q: "هل البيانات آمنة؟", a: "نعم، نستخدم أعلى معايير الأمان مع تشفير كامل للبيانات. كل حضانة لها بيانات معزولة بالكامل مع نظام صلاحيات متعدد المستويات (مدير، معلمة، ولي أمر، محاسب)." },
  { q: "هل يوجد خصم للدفع السنوي؟", a: "نعم، نوفر خصم ١٥٪ عند الاشتراك السنوي على جميع الباقات. كما نقدم خصومات خاصة للمجموعات (سلاسل الحضانات)." },
  { q: "كيف أتواصل مع الدعم الفني؟", a: "يمكنك فتح تذكرة دعم من داخل النظام مباشرة. باقة Basic توفر دعم بالبريد الإلكتروني، Standard بالواتساب، وPremium توفر كول سنتر خاص مع مدير حساب شخصي متاح 24/7." },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
};
const features = [
  // إدارية
  { icon: Users, title: "إدارة الموظفين", desc: "تتبع الحضور والغياب والرواتب والبدلات والمكافآت وتقييم الأداء بلوحة تحكم شاملة", category: "إداري" },
  { icon: Baby, title: "إدارة الأطفال", desc: "ملفات شاملة لكل طفل مع بيانات صحية وتاريخ ميلاد وصورة شخصية وربط بولي الأمر", category: "إداري" },
  { icon: BookOpen, title: "إدارة الفصول", desc: "تنظيم الفصول والسعة والفئات العمرية مع تعيين معلمة لكل فصل", category: "إداري" },
  { icon: QrCode, title: "حضور بـ QR Code", desc: "تسجيل حضور وانصراف الأطفال والموظفين عبر مسح كود QR مع تقارير تفصيلية", category: "إداري" },
  { icon: CalendarOff, title: "إدارة الإجازات", desc: "طلبات إجازات من أولياء الأمور مع موافقة أو رفض من الإدارة وتتبع كامل", category: "إداري" },
  { icon: Receipt, title: "الفواتير والمالية", desc: "إنشاء فواتير تلقائية وقوالب رسوم مخصصة مع تتبع المدفوعات والمتأخرات", category: "إداري" },
  { icon: BarChart3, title: "التقارير الإدارية", desc: "تقارير شاملة عن الحضور والأداء والمالية مع إمكانية التصدير لـ PDF و Excel", category: "إداري" },
  { icon: Bell, title: "الإشعارات الفورية", desc: "نظام إشعارات ذكي لكل الأحداث مع إمكانية البث الجماعي لأولياء الأمور", category: "إداري" },
  { icon: Wallet, title: "تقرير الرواتب", desc: "عرض رواتب الموظفين مع البدلات والخصومات والأرشفة الشهرية وتصدير كشوف المرتبات", category: "إداري" },
  { icon: BookOpenCheck, title: "السجل المالي الموحد", desc: "عرض شامل لجميع المعاملات المالية: فواتير، رواتب، مصروفات في مكان واحد", category: "إداري" },
  { icon: CreditCard, title: "إدارة الاشتراكات", desc: "خطط مرنة مع تجربة مجانية وإدارة ذاتية كاملة وتجديد تلقائي ومتابعة الحالة", category: "إداري" },
  // تعليمية
  { icon: GraduationCap, title: "مكتبة الدروس", desc: "رفع دروس بصيغ PDF وفيديو ونصوص مع معاينة مباشرة وربط بالفصول", category: "تعليمي" },
  { icon: FileText, title: "امتحانات أوتوماتيك", desc: "تحويل النصوص لأسئلة تلقائية بالذكاء الاصطناعي مع بنك أسئلة متكامل", category: "تعليمي" },
  { icon: PenTool, title: "الواجبات والتصحيح", desc: "واجبات مع رفع صور الكراسة وتصحيح أونلاين من المعلمة مع درجات وملاحظات", category: "تعليمي" },
  { icon: Video, title: "جلسات فيديو لايف", desc: "بث مباشر للدروس مع شات تفاعلي وتسجيل حضور الأطفال وإمكانية التسجيل", category: "تعليمي" },
  { icon: Gamepad2, title: "ألعاب تعليمية تفاعلية", desc: "ألعاب حروف عربية وإنجليزية وحساب وعلوم ومنطق مع مستويات صعوبة وتتبع أداء", category: "تعليمي" },
  { icon: Brain, title: "تقارير الذكاء الاصطناعي", desc: "تحليل أداء كل طفل مع توصيات تعليمية مخصصة وتقارير شهرية تلقائية", category: "تعليمي" },
  { icon: Zap, title: "الحساب الذهني", desc: "تدريب الأطفال على الجمع والطرح والضرب والقسمة بمراحل متدرجة وعداد وقت ونجوم", category: "تعليمي" },
  { icon: Music, title: "الفونكس الإنجليزي", desc: "تعلّم أصوات الحروف الإنجليزية ودمجها لتكوين كلمات مع تقييم النطق والسحب والإسقاط", category: "تعليمي" },
  { icon: Puzzle, title: "ألعاب كارتون تفاعلية", desc: "ألعاب أصوات حيوانات وتلوين وطبخ ومتاهات بتصميم كارتوني جذاب للأطفال", category: "تعليمي" },
  { icon: Eye, title: "متابعة الأطفال", desc: "ملاحظات صحية ونفسية وسلوكية لكل طفل مع تحليل ذكي وتقارير أسبوعية", category: "تعليمي" },
  { icon: Palette, title: "الأنشطة التكميلية", desc: "أنشطة سلوكية وصحية وإبداعية واجتماعية لبناء شخصية الطفل بشكل متكامل", category: "تعليمي" },
  { icon: Activity, title: "تحليلات الأداء المتقدمة", desc: "رسوم بيانية وتقارير مفصلة لأداء كل طفل مع مقارنات ومتابعة التطور", category: "تعليمي" },
  // أولياء الأمور والدعم
  { icon: Smartphone, title: "بوابة أولياء الأمور", desc: "تطبيق كامل لأولياء الأمور لمتابعة أطفالهم: دروس، واجبات، حضور، فواتير", category: "تواصل" },
  { icon: MessageSquare, title: "المراسلات الفورية", desc: "تواصل مباشر بين المعلمات وأولياء الأمور مع إشعارات فورية وسجل محادثات", category: "تواصل" },
  { icon: Headphones, title: "دعم فني متكامل", desc: "نظام تذاكر دعم فني مع كول سنتر ومتابعة الحالة وسجل مكالمات", category: "تواصل" },
  { icon: Lock, title: "أمان وصلاحيات", desc: "نظام أدوار متعدد (مدير، معلمة، ولي أمر، محاسب) مع صلاحيات محكمة لكل دور", category: "تواصل" },
  { icon: Camera, title: "تخصيص الهوية البصرية", desc: "تخصيص شعار الحضانة والألوان الأساسية لتعكس هويتك المميزة", category: "تواصل" },
];

const stats = [
  { value: 500, suffix: "+", label: "حضانة مسجلة", icon: Heart },
  { value: 10000, suffix: "+", label: "طفل مسجل", icon: Baby },
  { value: 2000, suffix: "+", label: "موظف نشط", icon: Users },
  { value: 99.9, suffix: "%", label: "وقت التشغيل", icon: Zap },
];


const testimonials = [
  { name: "سارة المطيري", role: "مديرة حضانة الأمل", text: "NurseryNet غيّرت طريقة إدارتنا بالكامل. توفر علينا ساعات من العمل اليومي." },
  { name: "أحمد الشهراني", role: "مالك مجموعة حضانات", text: "أفضل استثمار قمنا به. النظام سهل الاستخدام والدعم الفني ممتاز." },
  { name: "نورة العتيبي", role: "مشرفة تعليمية", text: "تقارير الحضور والأداء ساعدتنا في تحسين جودة التعليم بشكل ملحوظ." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.6 },
  }),
} as const;

// Animated counter component
const AnimatedCounter = ({ value, suffix = "", decimals = 0 }: { value: number; suffix?: string; decimals?: number }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(0, value, {
      duration: 2,
      ease: [0.25, 0.46, 0.45, 0.94],
      onUpdate: (v) => {
        if (decimals > 0) {
          setDisplay(v.toFixed(decimals));
        } else {
          setDisplay(Math.floor(v).toLocaleString("en"));
        }
      },
    });
    return () => controls.stop();
  }, [isInView, value, decimals]);

  return <span ref={ref}>{display}{suffix}</span>;
};

const Landing = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const heroBgY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 0.95]);

  return (
    <div className="min-h-screen font-cairo overflow-x-hidden">
      <Helmet>
        <title>NurseryNet | منصة إدارة الحضانات السحابية</title>
        <meta name="description" content="منصة سحابية لإدارة الحضانات: حضور QR، فواتير، رواتب، دروس تفاعلية، تواصل مع أولياء الأمور وذكاء اصطناعي." />
        <link rel="canonical" href="https://nurserynet.cloud/" />
        <meta property="og:title" content="NurseryNet | منصة إدارة الحضانات السحابية" />
        <meta property="og:description" content="منصة سحابية متكاملة لإدارة الحضانات ورياض الأطفال." />
        <meta property="og:url" content="https://nurserynet.cloud/" />
        <script type="application/ld+json">{JSON.stringify(FAQ_JSONLD)}</script>
      </Helmet>
      {/* Navbar */}
      <nav className="fixed top-0 right-0 left-0 z-50 bg-background/70 backdrop-blur-xl border-b border-border/50">
        <div className="container mx-auto flex items-center justify-between h-16 px-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl gradient-hero flex items-center justify-center shadow-lg shadow-primary/20">
              <Baby className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold text-foreground">NurseryNet</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors py-1 min-h-[24px] inline-flex items-center">المميزات</a>
            <a href="#pricing" className="hover:text-foreground transition-colors py-1 min-h-[24px] inline-flex items-center">الأسعار</a>
            <a href="#faq" className="hover:text-foreground transition-colors py-1 min-h-[24px] inline-flex items-center">أسئلة شائعة</a>
            <a href="#testimonials" className="hover:text-foreground transition-colors py-1 min-h-[24px] inline-flex items-center">آراء العملاء</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/install">
              <Button variant="outline" size="sm" className="gap-2">
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">تثبيت التطبيق</span>
              </Button>
            </Link>
            <Link to="/auth">
              <Button variant="ghost" size="sm">تسجيل الدخول</Button>
            </Link>
            <Link to="/auth">
              <Button size="sm" className="gradient-hero border-0 shadow-lg shadow-primary/25">ابدأ مجاناً</Button>
            </Link>
          </div>
        </div>
      </nav>

      <main>
      {/* Hero with Parallax */}
      <section ref={heroRef} className="relative pt-28 pb-24 overflow-hidden">
        {/* Parallax background decoration */}
        <motion.div className="absolute inset-0 -z-10" style={{ y: heroBgY }}>
          <motion.div
            className="absolute top-20 right-1/4 w-72 h-72 bg-primary/5 rounded-full blur-3xl"
            animate={{ scale: [1, 1.15, 1], x: [0, 20, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-0 left-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"
            animate={{ scale: [1, 1.2, 1], x: [0, -30, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/3 rounded-full blur-[100px]"
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>

        <motion.div className="container mx-auto px-4" style={{ y: heroY, opacity: heroOpacity, scale: heroScale }}>
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Badge variant="outline" className="mb-6 px-4 py-1.5 text-sm font-medium border-primary/30 bg-primary/5 text-primary">
                <Sparkles className="w-3.5 h-3.5 ml-1.5" />
                المنصة الأولى لإدارة الحضانات في المنطقة
              </Badge>
            </motion.div>

            <h1
              className="text-4xl sm:text-5xl md:text-7xl font-extrabold text-foreground leading-[1.15] mb-6"
            >
              أدر حضانتك
              <br />
              <motion.span
                className="bg-clip-text text-transparent gradient-hero inline-block"
                animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
                transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
                style={{ backgroundSize: "200% 200%" }}
              >
                بذكاء وسهولة
              </motion.span>
            </h1>

            <motion.p
              className="text-lg md:text-xl text-muted-foreground mb-10 leading-relaxed max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              منصة سحابية متكاملة تجمع إدارة الموظفين والأطفال والفصول والتواصل مع أولياء الأمور في مكان واحد
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Link to="/auth">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                  <Button size="lg" className="gradient-hero border-0 text-base px-10 h-13 shadow-xl shadow-primary/25 hover:shadow-2xl hover:shadow-primary/30 transition-shadow">
                    ابدأ الآن مجاناً
                    <ChevronLeft className="w-4 h-4 mr-2" />
                  </Button>
                </motion.div>
              </Link>
              <a href="#features">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                  <Button variant="outline" size="lg" className="text-base px-8 h-13 min-h-[44px]">
                    تعرّف على المميزات
                  </Button>
                </motion.div>
              </a>
            </motion.div>

            {/* Trust badges */}
            <motion.div
              className="mt-12 flex items-center justify-center gap-6 text-xs text-muted-foreground"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              {["تجربة مجانية 30 يوم", "بدون بطاقة ائتمان", "إلغاء في أي وقت"].map((t, i) => (
                <motion.span
                  key={i}
                  className="flex items-center gap-1.5"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + i * 0.15 }}
                >
                  <Check className="w-3.5 h-3.5 text-primary" />
                  {t}
                </motion.span>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Stats with animated counters */}
      <section className="py-16 gradient-hero relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%220.05%22%3E%3Cpath d=%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-50" />
        {/* Floating particles */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-primary-foreground/10 rounded-full"
            style={{ left: `${15 + i * 15}%`, top: `${20 + (i % 3) * 25}%` }}
            animate={{ y: [-20, 20, -20], opacity: [0.2, 0.6, 0.2] }}
            transition={{ duration: 3 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.5 }}
          />
        ))}
        <div className="container mx-auto px-4 relative">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                className="text-center"
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.5, type: "spring", stiffness: 100 }}
              >
                <motion.div
                  className="w-12 h-12 rounded-xl bg-primary-foreground/10 flex items-center justify-center mx-auto mb-3"
                  whileHover={{ rotate: 10, scale: 1.1 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <stat.icon className="w-6 h-6 text-primary-foreground" />
                </motion.div>
                <div className="text-3xl md:text-4xl font-extrabold text-primary-foreground">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} decimals={stat.value === 99.9 ? 1 : 0} />
                </div>
                <div className="text-primary-foreground/70 mt-1 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary/3 rounded-full blur-[120px] -translate-x-1/2" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-secondary/3 rounded-full blur-[100px] translate-x-1/3" />

        <div className="container mx-auto px-4 relative">
          <motion.div className="text-center mb-20" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Badge variant="outline" className="mb-4 px-4 py-1.5 text-xs border-primary/30 bg-primary/5 text-primary">
              <Sparkles className="w-3 h-3 ml-1.5" />
              المميزات
            </Badge>
            <h2 className="text-3xl md:text-5xl font-extrabold text-foreground mb-5">كل ما تحتاجه في منصة واحدة</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">أكثر من 28 أداة متكاملة لإدارة حضانتك بذكاء واحترافية</p>
          </motion.div>

          {(["إداري", "تعليمي", "تواصل"] as const).map((cat, catIndex) => {
            const catFeatures = features.filter(f => f.category === cat);
            const catLabels = { "إداري": "النظام الإداري", "تعليمي": "النظام التعليمي", "تواصل": "التواصل والدعم" };
            const catIcons = { "إداري": "🏢", "تعليمي": "📚", "تواصل": "💬" };
            const catColors = { "إداري": "from-primary/10 to-primary/5", "تعليمي": "from-secondary/10 to-secondary/5", "تواصل": "from-accent to-accent/50" };
            return (
              <motion.div
                key={cat}
                className="mb-16 last:mb-0"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: catIndex * 0.1 }}
              >
                <div className="flex items-center gap-4 mb-8">
                  <motion.div
                    className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${catColors[cat]} flex items-center justify-center text-2xl`}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    {catIcons[cat]}
                  </motion.div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground">{catLabels[cat]}</h3>
                    <p className="text-xs text-muted-foreground">{catFeatures.length} أدوات متكاملة</p>
                  </div>
                  <div className="flex-1 h-px bg-gradient-to-l from-transparent via-border/50 to-transparent" />
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {catFeatures.map((f, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 25, scale: 0.97 }}
                      whileInView={{ opacity: 1, y: 0, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.06, duration: 0.4, type: "spring", stiffness: 120 }}
                      whileHover={{ y: -8, transition: { duration: 0.25, type: "spring", stiffness: 300 } }}
                    >
                      <Card className="h-full shadow-sm hover:shadow-xl transition-all duration-300 group border-border/40 hover:border-primary/30 bg-card/80 backdrop-blur-sm relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <CardContent className="p-5 relative">
                          <div className="flex items-start gap-4">
                            <motion.div
                              className="w-12 h-12 rounded-2xl gradient-hero flex items-center justify-center shadow-lg shadow-primary/15 shrink-0"
                              whileHover={{ rotate: [0, -8, 8, 0], scale: 1.1 }}
                              transition={{ duration: 0.5 }}
                            >
                              <f.icon className="w-5.5 h-5.5 text-primary-foreground" />
                            </motion.div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-bold text-card-foreground mb-1 group-hover:text-primary transition-colors">{f.title}</h3>
                              <p className="text-muted-foreground text-xs leading-relaxed">{f.desc}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Why NurseryNet */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-4">لماذا NurseryNet؟</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">ثلاثة أسباب تجعلنا الخيار الأمثل لحضانتك</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-10 max-w-5xl mx-auto">
            {[
              { icon: Shield, title: "أمان بدرجة بنكية", desc: "بياناتك مشفرة ومعزولة بالكامل. كل حضانة لها قاعدة بيانات خاصة محمية بأعلى معايير الأمان.", color: "from-primary to-primary" },
              { icon: Globe, title: "سحابي بالكامل", desc: "ادخل من أي جهاز في أي مكان. لا تحتاج لتنصيب أو تحديثات يدوية - كل شيء يعمل تلقائياً.", color: "from-secondary to-secondary" },
              { icon: Zap, title: "سرعة وأداء عالي", desc: "واجهة سريعة الاستجابة مع تقارير فورية. تعامل مع آلاف السجلات بدون تأخير.", color: "from-primary to-secondary" },
            ].map((b, i) => (
              <motion.div
                key={i}
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.5 }}
              >
                <motion.div
                  className={`w-18 h-18 rounded-2xl bg-gradient-to-br ${b.color} flex items-center justify-center mx-auto mb-5 shadow-lg`}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <b.icon className="w-8 h-8 text-primary-foreground" />
                </motion.div>
                <h3 className="font-bold text-foreground text-xl mb-3">{b.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/[0.02] rounded-full blur-[100px]" />
        <div className="container mx-auto px-4 relative">
          <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Badge variant="outline" className="mb-4 px-4 py-1.5 text-xs border-primary/30 bg-primary/5 text-primary">
              <Star className="w-3 h-3 ml-1.5" />
              الأسعار
            </Badge>
            <h2 className="text-3xl md:text-5xl font-extrabold text-foreground mb-5">خطط تناسب كل حضانة</h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">ابدأ بالنظام الإداري وأضف ما تحتاجه — نموذج مرن يناسب الجميع</p>
          </motion.div>

          {/* Main Plans */}
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-20">
            {subscriptionPlans.map((plan, i) => {
              const Icon = plan.icon;
              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 40, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12, duration: 0.5, type: "spring", stiffness: 100 }}
                  whileHover={{ y: -8, transition: { duration: 0.3, type: "spring", stiffness: 200 } }}
                  className="relative"
                >
                  <Card className={`h-full relative overflow-hidden transition-all duration-300 ${
                    plan.popular
                      ? "border-primary border-2 shadow-2xl shadow-primary/15 scale-[1.04] z-10"
                      : "shadow-card border-border/50 hover:shadow-xl hover:border-primary/20"
                  }`}>
                    {plan.popular && (
                      <motion.div
                        className="absolute top-0 left-0 right-0 gradient-hero py-2 text-center text-xs font-bold text-primary-foreground flex items-center justify-center gap-1.5"
                        initial={{ y: -40 }}
                        whileInView={{ y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        الأكثر طلباً
                      </motion.div>
                    )}
                    {plan.badge && !plan.popular && (
                      <div className="absolute top-3 right-3">
                        <Badge variant="outline" className="text-[10px] font-normal">{plan.badge}</Badge>
                      </div>
                    )}
                    <CardContent className={`p-7 ${plan.popular ? "pt-14" : "pt-10"}`}>
                      <div className="flex items-center gap-3 mb-5">
                        <motion.div
                          className={`w-13 h-13 rounded-2xl flex items-center justify-center ${plan.popular ? "gradient-hero shadow-lg shadow-primary/20" : "bg-primary/10"}`}
                          whileHover={{ rotate: 10, scale: 1.1 }}
                          transition={{ type: "spring", stiffness: 300 }}
                        >
                          <Icon className={`w-6 h-6 ${plan.popular ? "text-primary-foreground" : "text-primary"}`} />
                        </motion.div>
                        <div>
                          <h3 className="text-lg font-bold text-card-foreground">{plan.name}</h3>
                          <p className="text-xs text-muted-foreground font-medium">{plan.subtitle}</p>
                        </div>
                      </div>
                      <div className="mb-6 pb-6 border-b border-border/50">
                        <div className="flex items-baseline gap-1">
                          <span className="text-4xl font-extrabold text-foreground">{plan.monthlyPrice.toLocaleString()}</span>
                          <span className="text-muted-foreground text-sm">ج.م/شهرياً</span>
                        </div>
                        <motion.p
                          className="text-xs text-primary mt-2 bg-primary/5 rounded-lg px-3 py-1.5 inline-block"
                          whileHover={{ scale: 1.02 }}
                        >
                          💰 أو {Math.round(plan.monthlyPrice * 12 * (1 - ANNUAL_DISCOUNT / 100)).toLocaleString()} ج.م/سنوياً (وفر {ANNUAL_DISCOUNT}%)
                        </motion.p>
                      </div>
                      <Link to="/auth">
                        <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                          <Button className={`w-full mb-6 h-11 ${plan.popular ? "gradient-hero border-0 shadow-lg shadow-primary/20" : ""}`} variant={plan.popular ? "default" : "outline"}>
                            ابدأ التجربة المجانية
                            <ChevronLeft className="w-4 h-4 mr-1" />
                          </Button>
                        </motion.div>
                      </Link>
                      <ul className="space-y-3">
                        {plan.features.map((f, fi) => (
                          <motion.li
                            key={fi}
                            className="flex items-center gap-2.5 text-sm text-muted-foreground"
                            initial={{ opacity: 0, x: 10 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.3 + fi * 0.04 }}
                          >
                            <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <Check className="w-3 h-3 text-primary" />
                            </div>
                            {f}
                          </motion.li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Add-ons */}
          <motion.div
            className="max-w-5xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-10">
              <motion.div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent mb-4"
                whileHover={{ scale: 1.03 }}
              >
                <Plus className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">إضافات مرنة</span>
              </motion.div>
              <h3 className="text-2xl font-bold text-foreground mb-2">خصّص باقتك بإضافات ذكية</h3>
              <p className="text-sm text-muted-foreground">ابدأ بالباقة الإدارية وأضف ما تحتاجه بسعر إضافي بسيط</p>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {addOns.map((addon, i) => {
                const AddonIcon = addon.icon;
                return (
                  <motion.div
                    key={addon.id}
                    initial={{ opacity: 0, y: 20, scale: 0.97 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1, type: "spring", stiffness: 120 }}
                    whileHover={{ y: -6, transition: { duration: 0.25 } }}
                  >
                    <Card className="h-full border-dashed border-border/70 hover:border-primary/30 transition-all duration-300 hover:shadow-lg group">
                      <CardContent className="p-5">
                        <div className="flex items-center gap-3 mb-4">
                          <motion.div
                            className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center"
                            whileHover={{ rotate: 10, scale: 1.1 }}
                          >
                            <AddonIcon className="w-5 h-5 text-primary" />
                          </motion.div>
                          <div>
                            <p className="font-semibold text-sm text-card-foreground group-hover:text-primary transition-colors">{addon.name}</p>
                            <p className="text-[11px] text-muted-foreground">{addon.description}</p>
                          </div>
                        </div>
                        <ul className="space-y-2 mb-4">
                          {addon.features.map((f, fi) => (
                            <li key={fi} className="flex items-center gap-2 text-xs text-muted-foreground">
                              <div className="w-4 h-4 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                <Check className="w-2.5 h-2.5 text-primary" />
                              </div>
                              {f}
                            </li>
                          ))}
                        </ul>
                        <div className="flex items-center justify-between pt-4 border-t border-border/50">
                          <span className="font-bold text-sm text-foreground">
                            <Plus className="w-3.5 h-3.5 inline ml-0.5 text-primary" />
                            {addon.monthlyPrice.toLocaleString()} ج.م/شهرياً
                          </span>
                          <Badge variant="secondary" className="text-[10px]">
                            مشمولة في {addon.includedIn.includes("standard") ? "Standard+" : "Premium"}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Badge variant="outline" className="mb-4 px-3 py-1 text-xs border-primary/30 bg-primary/5 text-primary">آراء العملاء</Badge>
            <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-4">ماذا يقول عملاؤنا</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="h-full shadow-card border-border/50">
                  <CardContent className="p-6">
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, si) => (
                        <Star key={si} className="w-4 h-4 fill-primary text-primary" />
                      ))}
                    </div>
                    <p className="text-card-foreground text-sm leading-relaxed mb-5">"{t.text}"</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full gradient-hero flex items-center justify-center text-primary-foreground font-bold text-sm">
                        {t.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-card-foreground text-sm">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{t.role}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24">
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Badge variant="outline" className="mb-4 px-3 py-1 text-xs border-primary/30 bg-primary/5 text-primary">أسئلة شائعة</Badge>
            <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-4">الأسئلة الأكثر شيوعاً</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">إجابات على كل ما يخطر في بالك عن NurseryNet</p>
          </motion.div>
          <motion.div
            className="max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Accordion type="single" collapsible className="space-y-3">
              {FAQ_ITEMS.map((faq, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                >
                  <AccordionItem value={`faq-${i}`} className="border border-border/50 rounded-xl px-5 bg-card shadow-sm data-[state=open]:shadow-card transition-shadow">
                    <AccordionTrigger className="text-sm font-semibold text-card-foreground hover:no-underline py-4 text-right">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-3xl mx-auto p-12 md:p-16 rounded-3xl gradient-hero text-center relative overflow-hidden"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%220.06%22%3E%3Cpath d=%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]" />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-extrabold text-primary-foreground mb-4">جاهز لتطوير حضانتك؟</h2>
              <p className="text-primary-foreground/80 mb-8 text-lg max-w-lg mx-auto">
                انضم لأكثر من 500 حضانة تدير أعمالها بذكاء مع NurseryNet
              </p>
              <Link to="/auth">
                <Button size="lg" className="bg-white text-foreground hover:bg-white/90 text-base px-10 h-13 shadow-xl font-bold">
                  ابدأ رحلتك الآن
                  <ArrowLeft className="w-4 h-4 mr-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="py-12 border-t border-border/50 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg gradient-hero flex items-center justify-center">
                <Baby className="w-4 h-4 text-primary-foreground" />
              </div>
              <span className="font-bold text-foreground">NurseryNet</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <a href="#features" className="hover:text-foreground transition-colors py-1 min-h-[24px] inline-flex items-center">المميزات</a>
              <a href="#pricing" className="hover:text-foreground transition-colors py-1 min-h-[24px] inline-flex items-center">الأسعار</a>
              <a href="#testimonials" className="hover:text-foreground transition-colors py-1 min-h-[24px] inline-flex items-center">آراء العملاء</a>
              <a href="tel:+201100235470" className="hover:text-foreground transition-colors py-1 min-h-[24px] inline-flex items-center gap-1.5" dir="ltr">
                <Smartphone className="w-4 h-4" />
                01100235470
              </a>
            </div>
            <p className="text-muted-foreground text-sm">© 2026 NurseryNet. جميع الحقوق محفوظة.</p>
          </div>
        </div>
      </footer>
      <WhatsAppButton />
    </div>
  );
};

export default Landing;
